import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Calculator, FileSpreadsheet, Check, X, Trash2, Plus, GitBranch, ChevronDown, ChevronUp, Settings2 } from 'lucide-react';

const FormulaBuilderDialog = ({
  open,
  onOpenChange,
  selectedPlatform,
  formulaConfig,
  setFormulaConfig,
  onUploadSample,
  onSave
}) => {
  // Conditional rules for gross salary calculation
  const [conditionalRules, setConditionalRules] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Load existing conditional rules from formulaConfig
  useEffect(() => {
    if (formulaConfig.conditional_rules && formulaConfig.conditional_rules.length > 0) {
      setConditionalRules(formulaConfig.conditional_rules);
    }
  }, [formulaConfig.conditional_rules]);

  // Add a new conditional rule
  const addConditionalRule = () => {
    const newRule = {
      id: Date.now(),
      name: `Condition ${conditionalRules.length + 1}`,
      condition_column: '',
      condition_operator: '>',
      condition_value: '0',
      formula: '',
      description: ''
    };
    setConditionalRules([...conditionalRules, newRule]);
  };

  // Update a conditional rule
  const updateConditionalRule = (id, field, value) => {
    setConditionalRules(conditionalRules.map(rule => 
      rule.id === id ? { ...rule, [field]: value } : rule
    ));
  };

  // Remove a conditional rule
  const removeConditionalRule = (id) => {
    setConditionalRules(conditionalRules.filter(rule => rule.id !== id));
  };

  // Save with conditional rules
  const handleSave = () => {
    const config = {
      ...formulaConfig,
      conditional_rules: conditionalRules.length > 0 ? conditionalRules : [],
      use_conditional: conditionalRules.length > 0
    };
    setFormulaConfig(config);
    onSave();
  };

  // Comparison operators
  const operators = [
    { value: '>', label: '>' },
    { value: '>=', label: '>=' },
    { value: '<', label: '<' },
    { value: '<=', label: '<=' },
    { value: '==', label: '==' },
    { value: '!=', label: '!=' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_empty', label: 'Not Empty' },
    { value: 'is_empty', label: 'Is Empty' }
  ];

  // Available columns (from detected or manual entry)
  const availableColumns = formulaConfig.detected_columns || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Formula Builder - {selectedPlatform?.name}
          </DialogTitle>
          <DialogDescription>
            Define salary calculation formulas. All options are optional - use what fits your platform.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="formula" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="formula" className="gap-1">
              <Calculator className="w-4 h-4" />
              Formula
            </TabsTrigger>
            <TabsTrigger value="conditions" className="gap-1">
              <GitBranch className="w-4 h-4" />
              Conditions
            </TabsTrigger>
            <TabsTrigger value="columns" className="gap-1">
              <FileSpreadsheet className="w-4 h-4" />
              Columns
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: Simple Formula */}
          <TabsContent value="formula" className="space-y-4">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-3">Gross Salary Formula</h4>
              
              {/* Quick Variables */}
              <div className="mb-3">
                <Label className="text-xs text-slate-600 mb-2 block">Quick insert (click to add):</Label>
                <div className="flex flex-wrap gap-1.5">
                  {/* Platform Rate Variables */}
                  <Badge variant="outline" className="bg-green-50 text-green-700 cursor-pointer hover:bg-green-100 text-xs py-1 px-2"
                    onClick={() => setFormulaConfig({...formulaConfig, formula: (formulaConfig.formula || '') + 'pickup_rate'})}>
                    pickup_rate ({selectedPlatform?.pickup_rate || 0})
                  </Badge>
                  <Badge variant="outline" className="bg-green-50 text-green-700 cursor-pointer hover:bg-green-100 text-xs py-1 px-2"
                    onClick={() => setFormulaConfig({...formulaConfig, formula: (formulaConfig.formula || '') + 'dropoff_rate'})}>
                    dropoff_rate ({selectedPlatform?.dropoff_rate || 0})
                  </Badge>
                  <span className="text-slate-300 mx-1">|</span>
                  {/* Math operators */}
                  {['+', '-', '*', '/', '(', ')'].map(op => (
                    <Badge 
                      key={op}
                      variant="outline" 
                      className="bg-blue-50 text-blue-700 cursor-pointer hover:bg-blue-100 text-xs py-1 px-2 font-mono"
                      onClick={() => setFormulaConfig({...formulaConfig, formula: (formulaConfig.formula || '') + ` ${op} `})}
                    >
                      {op}
                    </Badge>
                  ))}
                  {/* Column variables if available */}
                  {availableColumns.length > 0 && (
                    <>
                      <span className="text-slate-300 mx-1">|</span>
                      {availableColumns.slice(0, 5).map((col, idx) => (
                        <Badge 
                          key={idx}
                          variant="outline" 
                          className="cursor-pointer hover:bg-purple-100 text-xs py-1 px-2"
                          onClick={() => setFormulaConfig({...formulaConfig, formula: (formulaConfig.formula || '') + `[${col}]`})}
                        >
                          [{col}]
                        </Badge>
                      ))}
                      {availableColumns.length > 5 && (
                        <Badge variant="outline" className="text-xs py-1 px-2 text-slate-500">
                          +{availableColumns.length - 5} more in Columns tab
                        </Badge>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Formula Input */}
              <div className="flex gap-2">
                <Input
                  value={formulaConfig.formula || ''}
                  onChange={(e) => setFormulaConfig({...formulaConfig, formula: e.target.value})}
                  placeholder="e.g., [Total Delivered] * dropoff_rate"
                  className="font-mono flex-1"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setFormulaConfig({...formulaConfig, formula: ''})}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Use <code className="bg-slate-100 px-1 rounded">[Column Name]</code> for Excel columns, 
                <code className="bg-slate-100 px-1 rounded ml-1">dropoff_rate</code> for platform rates
              </p>
            </div>

            {/* Net Salary Formula */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-semibold text-slate-700 mb-3">Net Salary Formula (Optional)</h4>
              <Input
                value={formulaConfig.net_formula || ''}
                onChange={(e) => setFormulaConfig({...formulaConfig, net_formula: e.target.value})}
                placeholder="e.g., gross_salary - fines - salik - maintenance - loan_emi"
                className="font-mono"
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="text-xs text-slate-500">Deductions:</span>
                {['fines', 'salik', 'maintenance', 'loan_emi', 'gross_salary'].map(v => (
                  <Badge 
                    key={v}
                    variant="outline" 
                    className={`cursor-pointer text-xs py-0.5 px-1.5 ${v === 'gross_salary' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
                    onClick={() => setFormulaConfig({...formulaConfig, net_formula: (formulaConfig.net_formula || '') + v})}
                  >
                    {v}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Formula Preview */}
            {formulaConfig.formula && (
              <div className="bg-slate-900 text-green-400 p-3 rounded-lg font-mono text-sm">
                <p><span className="text-blue-400">gross_salary</span> = {formulaConfig.formula}</p>
                {formulaConfig.net_formula && (
                  <p className="mt-1"><span className="text-blue-400">net_salary</span> = {formulaConfig.net_formula}</p>
                )}
              </div>
            )}
          </TabsContent>

          {/* TAB 2: Conditional Rules */}
          <TabsContent value="conditions" className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
              <p className="text-sm text-amber-800">
                <strong>Conditional formulas</strong> let you define different salary calculations based on conditions.
                For example: minimum pay for employees with perfect attendance, OR per-order pay for others.
              </p>
            </div>

            {/* Conditional Rules List */}
            <div className="space-y-3">
              {conditionalRules.map((rule, index) => (
                <div key={rule.id} className="bg-white border border-purple-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge className={index === 0 ? "bg-blue-600" : "bg-purple-600"}>
                        {index === 0 ? 'IF' : 'ELSE IF'}
                      </Badge>
                      <Input
                        value={rule.name}
                        onChange={(e) => updateConditionalRule(rule.id, 'name', e.target.value)}
                        className="h-7 w-40 text-sm"
                        placeholder="Rule name"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeConditionalRule(rule.id)}
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Condition Row */}
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Input
                      value={rule.condition_column}
                      onChange={(e) => updateConditionalRule(rule.id, 'condition_column', e.target.value)}
                      className="h-8 w-40 text-sm"
                      placeholder="Column name"
                    />
                    <Select 
                      value={rule.condition_operator} 
                      onValueChange={(v) => updateConditionalRule(rule.id, 'condition_operator', v)}
                    >
                      <SelectTrigger className="h-8 w-28 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {operators.map((op) => (
                          <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={rule.condition_value}
                      onChange={(e) => updateConditionalRule(rule.id, 'condition_value', e.target.value)}
                      className="h-8 w-24 text-sm"
                      placeholder="Value"
                      disabled={rule.condition_operator === 'not_empty' || rule.condition_operator === 'is_empty'}
                    />
                    <span className="text-slate-500 text-sm">→</span>
                    <Input
                      value={rule.formula}
                      onChange={(e) => updateConditionalRule(rule.id, 'formula', e.target.value)}
                      className="h-8 flex-1 text-sm font-mono min-w-[200px]"
                      placeholder="Formula (e.g., [Orders] * dropoff_rate)"
                    />
                  </div>

                  {/* Description */}
                  <Input
                    value={rule.description}
                    onChange={(e) => updateConditionalRule(rule.id, 'description', e.target.value)}
                    className="h-7 text-xs text-slate-500"
                    placeholder="Optional description (e.g., Minimum guaranteed pay for perfect attendance)"
                  />
                </div>
              ))}

              {/* Add Condition Button */}
              <Button 
                variant="outline" 
                onClick={addConditionalRule}
                className="w-full border-dashed border-purple-300 text-purple-600 hover:bg-purple-50"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Condition
              </Button>

              {/* Default Formula */}
              {conditionalRules.length > 0 && (
                <div className="bg-slate-100 border border-slate-300 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="bg-slate-200">DEFAULT (ELSE)</Badge>
                    <span className="text-sm text-slate-600">When no conditions match:</span>
                  </div>
                  <Input
                    value={formulaConfig.formula || ''}
                    onChange={(e) => setFormulaConfig({...formulaConfig, formula: e.target.value})}
                    className="font-mono text-sm"
                    placeholder="Default formula"
                  />
                </div>
              )}

              {/* Selection Mode */}
              {conditionalRules.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <Label className="text-sm font-medium text-blue-800 mb-2 block">
                    When multiple conditions match, use:
                  </Label>
                  <Select 
                    value={formulaConfig.selection_mode || 'first_match'} 
                    onValueChange={(v) => setFormulaConfig({...formulaConfig, selection_mode: v})}
                  >
                    <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="first_match">First matching condition</SelectItem>
                      <SelectItem value="max_value">Maximum value</SelectItem>
                      <SelectItem value="min_value">Minimum value</SelectItem>
                      <SelectItem value="user_select">Let user select during processing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Conditional Preview */}
            {conditionalRules.length > 0 && (
              <div className="bg-slate-900 text-green-400 p-3 rounded-lg font-mono text-xs">
                {conditionalRules.map((rule, idx) => (
                  <div key={rule.id}>
                    <span className="text-blue-400">{idx === 0 ? 'IF' : 'ELSE IF'}</span>
                    <span className="text-yellow-400"> [{rule.condition_column || '?'}] {rule.condition_operator} {rule.condition_value}</span>
                    <span className="text-slate-400"> → </span>
                    <span className="text-green-400">{rule.formula || '...'}</span>
                  </div>
                ))}
                <div>
                  <span className="text-blue-400">ELSE</span>
                  <span className="text-slate-400"> → </span>
                  <span className="text-green-400">{formulaConfig.formula || 'default'}</span>
                </div>
              </div>
            )}
          </TabsContent>

          {/* TAB 3: Column Detection (Optional) */}
          <TabsContent value="columns" className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
              <p className="text-sm text-blue-800">
                <strong>Optional:</strong> Upload a sample Excel file to auto-detect column headers. 
                You can also type column names directly in formulas using <code className="bg-blue-100 px-1 rounded">[Column Name]</code> syntax.
              </p>
            </div>

            {/* Upload Sample */}
            <div className="border border-slate-200 rounded-lg p-4">
              <Label className="text-sm font-medium mb-2 block">Upload Sample Payout Sheet</Label>
              <Input
                type="file"
                accept=".xlsx,.xls,.csv,.xlsm,.xlsb"
                onChange={(e) => {
                  if (e.target.files[0] && selectedPlatform) {
                    onUploadSample(selectedPlatform.id, e.target.files[0]);
                  }
                }}
              />
            </div>

            {/* Detected Columns */}
            {availableColumns.length > 0 && (
              <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                <Label className="text-sm font-medium text-green-800 mb-2 block">
                  Detected Columns ({availableColumns.length})
                </Label>
                <div className="flex flex-wrap gap-2">
                  {availableColumns.map((col, idx) => {
                    const isSelected = (formulaConfig.selected_columns || []).includes(col);
                    const isRiderId = formulaConfig.rider_id_column === col;
                    return (
                      <Badge 
                        key={idx} 
                        variant={isSelected || isRiderId ? "default" : "outline"}
                        className={`cursor-pointer text-sm py-1 px-2 ${
                          isRiderId 
                            ? 'bg-purple-500 text-white' 
                            : isSelected 
                              ? 'bg-green-500 text-white' 
                              : ''
                        }`}
                        onClick={() => {
                          if (isRiderId) return;
                          const selected = formulaConfig.selected_columns || [];
                          setFormulaConfig({
                            ...formulaConfig,
                            selected_columns: isSelected 
                              ? selected.filter(c => c !== col)
                              : [...selected, col]
                          });
                        }}
                      >
                        {isRiderId && '🆔 '}
                        {isSelected && !isRiderId && '✓ '}
                        {col}
                      </Badge>
                    );
                  })}
                </div>

                {/* Rider ID Selection */}
                <div className="mt-4 pt-3 border-t border-green-200">
                  <Label className="text-sm text-green-700 mb-2 block">Rider ID Column:</Label>
                  <Select 
                    value={formulaConfig.rider_id_column || ''} 
                    onValueChange={(v) => setFormulaConfig({...formulaConfig, rider_id_column: v})}
                  >
                    <SelectTrigger className="w-full max-w-xs bg-white">
                      <SelectValue placeholder="Select Rider ID column" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableColumns.map((col, idx) => (
                        <SelectItem key={idx} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Manual Column Entry */}
            <div className="border border-slate-200 rounded-lg p-4">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              >
                <Label className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                  <Settings2 className="w-4 h-4" />
                  Advanced Options
                </Label>
                {showAdvancedOptions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
              
              {showAdvancedOptions && (
                <div className="mt-3 pt-3 border-t space-y-3">
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Manual Rider ID Column Name</Label>
                    <Input
                      value={formulaConfig.rider_id_column || ''}
                      onChange={(e) => setFormulaConfig({...formulaConfig, rider_id_column: e.target.value})}
                      placeholder="e.g., Platform ID, Rider Code"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Custom Variables (comma separated)</Label>
                    <Input
                      value={formulaConfig.custom_variables || ''}
                      onChange={(e) => setFormulaConfig({...formulaConfig, custom_variables: e.target.value})}
                      placeholder="e.g., bonus_rate, incentive_multiplier"
                      className="text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} className="gap-2 bg-green-600 hover:bg-green-700">
            <Check className="w-4 h-4" />
            Save Configuration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FormulaBuilderDialog;
